import { useCallback, useEffect, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@constructionautomation/backend";
import { toast } from "sonner";

import { notifyError } from "@/lib/notify-error";
import type { OrganizationInvitation } from "./member-rows";

/**
 * Organization invitations live in WorkOS, so they're listed by an action rather than a reactive
 * query. The list reloads after every invitation change made here.
 */
export function useOrganizationInvitations() {
  const listInvitations = useAction(api.authActions.listOrganizationInvitations);
  const resendInvitation = useAction(api.authActions.resendOrganizationInvitation);
  const revokeInvitation = useAction(api.authActions.revokeOrganizationInvitation);
  const [invitations, setInvitations] = useState<OrganizationInvitation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workingIds, setWorkingIds] = useState<ReadonlySet<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      setInvitations(await listInvitations({}));
      setError(null);
    } catch (listError) {
      console.error("List invitations error:", listError);
      setInvitations([]);
      setError("Invitations couldn't load. Refresh the page to try again.");
      notifyError(listError, "Failed to load invitations");
    }
  }, [listInvitations]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const runForInvitation = useCallback(
    async (invitationId: string, run: () => Promise<unknown>, success: string, failure: string) => {
      setWorkingIds((current) => new Set(current).add(invitationId));
      try {
        await run();
        toast.success(success);
        await refresh();
      } catch (actionError) {
        notifyError(actionError, failure);
        console.error(`${failure}:`, actionError);
      } finally {
        setWorkingIds((current) => {
          const next = new Set(current);
          next.delete(invitationId);
          return next;
        });
      }
    },
    [refresh]
  );

  const resend = useCallback(
    (invitation: OrganizationInvitation) =>
      runForInvitation(
        invitation.id,
        () => resendInvitation({ invitationId: invitation.id }),
        `Invitation resent to ${invitation.email}`,
        "Failed to resend invitation"
      ),
    [resendInvitation, runForInvitation]
  );

  const revoke = useCallback(
    (invitation: OrganizationInvitation) =>
      runForInvitation(
        invitation.id,
        () => revokeInvitation({ invitationId: invitation.id }),
        `Invitation to ${invitation.email} revoked`,
        "Failed to revoke invitation"
      ),
    [revokeInvitation, runForInvitation]
  );

  return { invitations, error, workingIds, refresh, resend, revoke };
}
